import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, SkipForward } from "lucide-react";

/**
 * Hidden YouTube iframe that plays a track quietly in the background,
 * with a small floating mute/unmute pill in the corner.
 * Requires a prior user gesture before `autoplay` works in most browsers.
 */
const AmbientPlayer = ({ tracks, autoplay = false, volume = 20 }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [ready, setReady] = useState(false);
  const [idx, setIdx] = useState(0);
  const idxRef = useRef(0);

  const playNext = (p) => {
    const next = (idxRef.current + 1) % tracks.length;
    idxRef.current = next;
    setIdx(next);
    p.loadVideoById(tracks[next].id);
  };

  useEffect(() => {
    const initPlayer = () => {
      if (!containerRef.current || playerRef.current) return;
      playerRef.current = new window.YT.Player(containerRef.current, {
        height: "0",
        width: "0",
        videoId: tracks[0].id,
        playerVars: {
          autoplay: autoplay ? 1 : 0,
          controls: 0,
          playsinline: 1,
          disablekb: 1,
          modestbranding: 1,
          rel: 0,
          iv_load_policy: 3,
        },
        events: {
          onReady: (e) => {
            try {
              e.target.setVolume(volume);
              if (autoplay) e.target.playVideo();
            } catch (_) {}
            setReady(true);
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.ENDED) {
              playNext(e.target);
            }
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
      }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (prev) prev();
        initPlayer();
      };
    }

    return () => {
      try {
        if (playerRef.current && playerRef.current.destroy) {
          playerRef.current.destroy();
        }
      } catch (_) {}
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => {
    const p = playerRef.current;
    if (!p) return;
    try {
      if (muted) {
        p.unMute();
        p.setVolume(volume);
        setMuted(false);
      } else {
        p.mute();
        setMuted(true);
      }
    } catch (_) {}
  };

  return (
    <>
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: 0,
          bottom: 0,
          width: 0,
          height: 0,
          overflow: "hidden",
          pointerEvents: "none",
          opacity: 0,
        }}
      >
        <div ref={containerRef} />
      </div>
      {ready && (
        <div className="fixed bottom-5 right-5 z-50 inline-flex items-center rounded-full bg-black/60 backdrop-blur-md border border-white/15 text-neutral-200 text-xs shadow-lg">
          <button
            onClick={toggle}
            data-testid="ambient-player-toggle"
            aria-label={muted ? "Unmute background music" : "Mute background music"}
            className="inline-flex items-center gap-2 pl-4 pr-3 py-2.5 hover:text-white transition-colors"
          >
            {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            <span className="hidden sm:inline" data-testid="ambient-player-label">{tracks[idx].label}</span>
            <span className="sm:hidden">{muted ? "Muted" : "Playing"}</span>
          </button>
          {tracks.length > 1 && (
            <button
              onClick={() => playerRef.current && playNext(playerRef.current)}
              data-testid="ambient-player-skip"
              aria-label="Skip to next song"
              className="pl-2 pr-4 py-2.5 border-l border-white/15 hover:text-white transition-colors"
            >
              <SkipForward size={14} />
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default AmbientPlayer;
